import { useEffect, useRef } from 'react';

/**
 * Fires `onChange` when the FX currency switches to a different non-empty value.
 *
 * The previous currency is tracked in a ref initialized to undefined, so the
 * very first currency population (mount or operation reinit) does NOT trigger
 * the callback — only a genuine user-driven corridor change does.
 *
 * Used by both the form-state hook (to clear beneficiary fields/errors) and the
 * form component (to reset BIC validation), which react to the same trigger but
 * act on separate state stores.
 */
export const useOnCurrencyChange = (
	currency: string | undefined,
	onChange: () => void,
) => {
	const prevCurrencyRef = useRef<string | undefined>(undefined);

	useEffect(() => {
		const prev = prevCurrencyRef.current;
		prevCurrencyRef.current = currency;

		// Only fire when we had a real previous currency AND it changed.
		if (!prev || !currency || prev === currency) {
			return;
		}

		onChange();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currency]);
};
